import type { Conversation } from '@/features/conversations/conversations.api';

/** The grouping key for same-phone adjacency: the phone number when known,
 *  else the stable platform peer id. Mirrors the backend's
 *  `COALESCE(phone_e164, peer_id)` (see `list_conversations` `group=phone`).
 *  Exported so the realtime projector hoists exactly the block the renderer
 *  draws (see `projection.ts`). */
export function groupKeyOf(conversation: Conversation): string {
  return conversation.contact.phone_e164 ?? conversation.contact.peer_id;
}

/**
 * One entry in the aggregate inbox's rendered list (feat/merged-phone-thread):
 * either an ordinary per-conversation row, or a MERGED row standing in for a
 * whole same-phone run (2+ conversations sharing the group key, collapsed to
 * one visual row that opens the merged thread).
 */
export type DisplayRow =
  | { kind: 'single'; conversation: Conversation }
  | {
      kind: 'merged';
      /** The run's grouping key — `groupKeyOf` of every member; also the
       *  thread key `setSelectedThread` / `GET /threads/{key}/messages` use. */
      key: string;
      /** The run's conversations in SERVER ORDER (block head first — the
       *  grouped ORDER BY puts the newest unpinned member at the head of an
       *  unpinned block, but orders a fully-PINNED block by the pin anchor,
       *  newest PIN first: pin order, NOT activity order — the renderer
       *  must not treat the head as "freshest"). Always length >= 2. */
      members: Conversation[];
    };

/**
 * Collapse each contiguous same-phone run into one merged display row
 * (aggregate "所有账号" view only — per-account lists never call this).
 *
 * Pure and order-preserving: the server's `group=phone` ordering is the
 * single ordering authority — it already placed same-key rows adjacently
 * (unpinned blocks by recency; pinned same-key rows contiguously via the
 * GROUP-LEVEL PIN ANCHOR, feat/merged-pin-and-special-filter) — and this
 * walks the already-ordered `items` WITHOUT re-sorting. A run only fuses
 * rows that share BOTH the key AND pinnedness: a block is uniformly pinned
 * or uniformly unpinned, never mixed. A customer whose same-key rows are
 * ALL pinned therefore stays ONE merged block inside the pinned band; a
 * partially-pinned customer splits exactly as before — the pinned member(s)
 * form their own pinned block/single while the unpinned members stay a
 * normal block (mirrors the server: pinned rows leave the unpinned group's
 * aggregates — `FILTER (WHERE pinned_at IS NULL)`). The WS group-block
 * hoist (`hoistGroupBlock` in `projection.ts`) moves only UNPINNED blocks —
 * pinned blocks never move on recency (the pin anchor is pin-time based) —
 * so the projector and this collapse always agree on membership.
 *
 * Single-member "runs" (pinned or not) come back as `'single'` entries and
 * render exactly as today.
 */
export function collapseGroupRuns(items: Conversation[]): DisplayRow[] {
  const rows: DisplayRow[] = [];
  let i = 0;
  while (i < items.length) {
    const item = items[i];
    const key = groupKeyOf(item);
    const pinned = item.pinned_at !== null;
    let end = i + 1;
    while (
      end < items.length &&
      (items[end].pinned_at !== null) === pinned &&
      groupKeyOf(items[end]) === key
    ) {
      end += 1;
    }
    if (end - i >= 2) {
      rows.push({ kind: 'merged', key, members: items.slice(i, end) });
    } else {
      rows.push({ kind: 'single', conversation: item });
    }
    i = end;
  }
  return rows;
}
